// Stagger.js
// StaggerGroup reveals its StaggerItem children one after another, either
// as soon as it mounts (e.g. a hero section) or the first time each one
// scrolls into view (e.g. a grid of cards). Each item is given its own
// explicit delay based on its position, then triggers independently —
// this keeps the cascade reliable regardless of how deep the items are
// nested inside other wrappers.

import React, { Children, cloneElement, isValidElement } from 'react';
import { motion } from 'motion/react';
import { fadeUpVariants } from './variants';

export const StaggerGroup = ({
    children,
    as = 'div',
    className,
    staggerChildren = 0.15,
    delayChildren = 0,
    scroll = false,
    amount = 0.2,
    once = true,
    ...rest
}) => {
    const Component = motion[as];

    let index = 0;
    const items = Children.map(children, (child) => {
        // Only StaggerItem children participate in the cascade — other
        // children (e.g. a section title using its own Reveal) pass through
        // untouched instead of getting our internal props spread onto them.
        if (!isValidElement(child) || child.type !== StaggerItem) return child;
        const delay = delayChildren + index * staggerChildren;
        index += 1;
        return cloneElement(child, {
            _staggerDelay: delay,
            _scroll: scroll,
            _once: once,
            _amount: amount,
        });
    });

    return (
        <Component className={className} {...rest}>
            {items}
        </Component>
    );
};

export const StaggerItem = ({
    children,
    as = 'div',
    className,
    y = 24,
    duration = 0.6,
    _staggerDelay = 0,
    _scroll = false,
    _once = true,
    _amount = 0.2,
    ...rest
}) => {
    const Component = motion[as];
    const triggerProps = _scroll
        ? { whileInView: 'show', viewport: { once: _once, amount: _amount } }
        : { animate: 'show' };

    return (
        <Component
            className={className}
            variants={fadeUpVariants(y, duration, _staggerDelay)}
            initial="hidden"
            {...triggerProps}
            {...rest}
        >
            {children}
        </Component>
    );
};
